// src/lib/affiliateSchema.js
// Affiliate Program Database Schema and Utilities

import { db } from './firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';

/**
 * Affiliate Program Constants
 */
export const AFFILIATE_TIERS = {
  BRONZE: 'bronze',
  SILVER: 'silver', 
  GOLD: 'gold',
  PLATINUM: 'platinum'
};

export const AFFILIATE_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  PENDING: 'pending'
};

export const COMMISSION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  PAID: 'paid'
};

export const PAYOUT_METHODS = {
  GCASH: 'gcash',
  PAYMAYA: 'paymaya',
  BANK: 'bank'
};

/**
 * Generate unique affiliate ID
 */
export function generateAffiliateId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  return `AFF${timestamp}${random}`.toUpperCase();
}

/**
 * Generate affiliate referral code
 */
export function generateReferralCode(userId) {
  const shortId = userId.substring(0, 8).toUpperCase();
  const random = Math.random().toString(36).substr(2, 4).toUpperCase();
  return `${shortId}${random}`;
}

/**
 * Create affiliate profile for new user
 */
export async function createAffiliateProfile(userId, userData) {
  try {
    const affiliateId = generateAffiliateId();
    const referralCode = generateReferralCode(userId);
    
    const affiliateData = {
      userId,
      affiliateId,
      referralCode,
      tier: AFFILIATE_TIERS.BRONZE,
      commissionRate: 0.05, // 5% default commission
      totalEarnings: 0,
      totalSales: 0,
      referralCount: 0,
      status: AFFILIATE_STATUS.ACTIVE,
      createdAt: serverTimestamp(),
      lastActivity: serverTimestamp(),
      preferences: {
        notifications: true,
        autoPayout: false,
        payoutMethod: PAYOUT_METHODS.GCASH,
        payoutThreshold: 500 // Minimum ₱500 for payout
      },
      payout: {
        preferredMethod: PAYOUT_METHODS.GCASH,
        gcashNumber: '',
        paymayaNumber: '',
        bankAccount: '',
        bankName: '',
        accountName: '',
        minimumPayout: 500,
        autoPayout: false
      },
      // User data for quick access
      userInfo: {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phoneNumber
      }
    };

    await setDoc(doc(db, 'affiliates', affiliateId), affiliateData);
    
    // Also create a lookup document for quick access by userId
    await setDoc(doc(db, 'affiliateLookup', userId), {
      affiliateId,
      referralCode,
      status: AFFILIATE_STATUS.ACTIVE,
      createdAt: serverTimestamp()
    });

    return affiliateData;
  } catch (error) {
    console.error('Error creating affiliate profile:', error);
    throw error;
  }
}

/**
 * Get affiliate profile by userId
 */
export async function getAffiliateProfile(userId) {
  try {
    // First try the lookup collection for quick access
    const lookupRef = doc(db, 'affiliateLookup', userId);
    const lookupSnap = await getDoc(lookupRef);
    
    if (lookupSnap.exists()) {
      const lookupData = lookupSnap.data();
      const affiliateRef = doc(db, 'affiliates', lookupData.affiliateId);
      const affiliateSnap = await getDoc(affiliateRef);
      
      if (affiliateSnap.exists()) {
        return { id: affiliateSnap.id, ...affiliateSnap.data() };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting affiliate profile:', error);
    throw error;
  }
}

/**
 * Update affiliate statistics
 */
export async function updateAffiliateStats(affiliateId, updates) {
  try {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    await updateDoc(affiliateRef, {
      ...updates,
      lastActivity: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating affiliate stats:', error);
    throw error;
  }
}

/**
 * Update affiliate profile fields (admin quick edit)
 * - Supports updating tier, status, commissionRate, and userInfo
 * - Will also propagate status/referralCode to affiliateLookup
 */
export async function updateAffiliateProfile(affiliateId, updates) {
  try {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    const snap = await getDoc(affiliateRef);
    if (!snap.exists()) throw new Error('Affiliate not found');
    const data = snap.data();

    const toUpdate = { ...updates, updatedAt: serverTimestamp() };
    await updateDoc(affiliateRef, toUpdate);

    // Propagate to lookup if needed
    const userId = data.userId;
    if (userId) {
      const lookupRef = doc(db, 'affiliateLookup', userId);
      const lookupUpdates = {};
      if (typeof updates.status === 'string') lookupUpdates.status = updates.status;
      if (typeof updates.referralCode === 'string') lookupUpdates.referralCode = updates.referralCode;
      if (Object.keys(lookupUpdates).length > 0) {
        await updateDoc(lookupRef, lookupUpdates).catch(() => {/* ignore if lookup missing */});
      }
    }

    return true;
  } catch (error) {
    console.error('Error updating affiliate profile:', error);
    throw error;
  }
}

/**
 * Create commission record
 */
export async function createCommissionRecord(data) {
  try {
    const commissionData = {
      affiliateId: data.affiliateId,
      orderId: data.orderId,
      productId: data.productId,
      productName: data.productName,
      commissionAmount: data.commissionAmount,
      commissionRate: data.commissionRate,
      orderTotal: data.orderTotal,
      status: COMMISSION_STATUS.PENDING,
      createdAt: serverTimestamp(),
      paidAt: null
    };

    const commissionRef = doc(collection(db, 'affiliateCommissions'));
    await setDoc(commissionRef, commissionData);
    
    return commissionRef.id;
  } catch (error) {
    console.error('Error creating commission record:', error);
    throw error;
  }
}

/**
 * Track referral
 */
export async function trackReferral(affiliateId, referredUserId, referralCode) {
  try {
    const referralData = {
      affiliateId,
      referredUserId,
      referralCode,
      status: 'pending',
      createdAt: serverTimestamp(),
      firstPurchaseAt: null
    };

    const referralRef = doc(collection(db, 'affiliateReferrals'));
    await setDoc(referralRef, referralData);
    
    return referralRef.id;
  } catch (error) {
    console.error('Error tracking referral:', error);
    throw error;
  }
}

/**
 * Get affiliate commissions
 */
export async function getAffiliateCommissions(affiliateId, limit = 50) {
  try {
    const q = query(
      collection(db, 'affiliateCommissions'),
      where('affiliateId', '==', affiliateId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting affiliate commissions:', error);
    throw error;
  }
}

/**
 * Get affiliate referrals
 */
export async function getAffiliateReferrals(affiliateId, limit = 50) {
  try {
    const q = query(
      collection(db, 'affiliateReferrals'),
      where('affiliateId', '==', affiliateId),
      orderBy('createdAt', 'desc'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting affiliate referrals:', error);
    throw error;
  }
}

/**
 * Calculate commission for product
 */
export function calculateCommission(product, affiliate, quantity = 1) {
  if (!product.affiliateSettings?.enabled) {
    return 0;
  }

  const baseCommission = product.affiliateSettings.commissionValue;
  const tierMultiplier = product.affiliateSettings.tierMultipliers?.[affiliate.tier] || 1;
  
  let commission = 0;
  
  if (product.affiliateSettings.commissionType === 'percentage') {
    commission = (product.price * baseCommission * tierMultiplier * quantity) / 100;
  } else {
    commission = baseCommission * tierMultiplier * quantity;
  }
  
  // Apply min/max limits
  const minCommission = product.affiliateSettings.minCommission || 0;
  const maxCommission = product.affiliateSettings.maxCommission || Infinity;
  
  return Math.max(minCommission, Math.min(commission, maxCommission));
}

/**
 * Create affiliate link
 */
export function createAffiliateLink(baseUrl, referralCode, productId = null) {
  const url = new URL(baseUrl);
  url.searchParams.set('ref', referralCode);
  if (productId) {
    url.searchParams.set('product', productId);
  }
  return url.toString();
}

/**
 * Validate affiliate code
 */
export async function validateAffiliateCode(referralCode) {
  try {
    const q = query(
      collection(db, 'affiliateLookup'),
      where('referralCode', '==', referralCode)
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    
    const lookupData = snapshot.docs[0].data();
    return {
      userId: snapshot.docs[0].id,
      affiliateId: lookupData.affiliateId,
      status: lookupData.status
    };
  } catch (error) {
    console.error('Error validating affiliate code:', error);
    throw error;
  }
}

/**
 * Database Schema Documentation
 */
export const AFFILIATE_SCHEMA = {
  // Main affiliate profiles
  affiliates: {
    description: 'Main affiliate profiles with performance data',
    fields: {
      userId: 'string - Firebase Auth UID',
      affiliateId: 'string - Unique affiliate identifier',
      referralCode: 'string - Unique referral code',
      tier: 'string - bronze|silver|gold|platinum',
      commissionRate: 'number - Default commission rate (0-1)',
      totalEarnings: 'number - Total earnings in pesos',
      totalSales: 'number - Total sales generated',
      referralCount: 'number - Number of successful referrals',
      status: 'string - active|suspended|pending',
      createdAt: 'timestamp - When affiliate was created',
      lastActivity: 'timestamp - Last activity timestamp',
      preferences: {
        notifications: 'boolean - Email notifications enabled',
        autoPayout: 'boolean - Automatic payout enabled',
        payoutMethod: 'string - gcash|paymaya|bank',
        payoutThreshold: 'number - Minimum payout amount'
      },
      payout: {
        preferredMethod: 'string - Preferred payout method',
        gcashNumber: 'string - GCASH number',
        paymayaNumber: 'string - PAYMAYA number',
        bankAccount: 'string - Bank account number',
        bankName: 'string - Bank name',
        accountName: 'string - Account holder name',
        minimumPayout: 'number - Minimum payout amount',
        autoPayout: 'boolean - Automatic payout enabled'
      },
      userInfo: {
        firstName: 'string - User first name',
        lastName: 'string - User last name',
        email: 'string - User email',
        phone: 'string - User phone number'
      }
    }
  },

  // Quick lookup by userId
  affiliateLookup: {
    description: 'Quick lookup table for affiliate data by userId',
    fields: {
      affiliateId: 'string - Reference to affiliates collection',
      referralCode: 'string - User referral code',
      status: 'string - Current status',
      createdAt: 'timestamp - Creation timestamp'
    }
  },

  // Commission tracking
  affiliateCommissions: {
    description: 'Individual commission records',
    fields: {
      affiliateId: 'string - Reference to affiliate',
      orderId: 'string - Reference to order',
      productId: 'string - Reference to product',
      productName: 'string - Product name for display',
      commissionAmount: 'number - Commission amount in pesos',
      commissionRate: 'number - Commission rate used',
      orderTotal: 'number - Total order amount',
      status: 'string - pending|approved|paid',
      createdAt: 'timestamp - When commission was created',
      paidAt: 'timestamp - When commission was paid'
    }
  },

  // Referral tracking
  affiliateReferrals: {
    description: 'Referral tracking records',
    fields: {
      affiliateId: 'string - Reference to affiliate',
      referredUserId: 'string - Referred user UID',
      referralCode: 'string - Referral code used',
      status: 'string - pending|active',
      createdAt: 'timestamp - When referral was created',
      firstPurchaseAt: 'timestamp - When first purchase was made'
    }
  },

  // Live streaming sessions
  liveSessions: {
    description: 'Live selling session records',
    fields: {
      affiliateId: 'string - Reference to affiliate',
      platform: 'string - facebook|youtube|native',
      streamId: 'string - Platform stream ID',
      title: 'string - Stream title',
      description: 'string - Stream description',
      startTime: 'timestamp - When stream started',
      endTime: 'timestamp - When stream ended',
      viewerCount: 'number - Peak viewer count',
      salesGenerated: 'number - Sales from this stream',
      commissionEarned: 'number - Commission from this stream',
      status: 'string - active|ended|cancelled'
    }
  },

  // Affiliate links performance
  affiliateLinks: {
    description: 'Affiliate link performance tracking',
    fields: {
      affiliateId: 'string - Reference to affiliate',
      linkUrl: 'string - The affiliate link',
      productId: 'string - Optional product reference',
      clicks: 'number - Number of clicks',
      conversions: 'number - Number of conversions',
      sales: 'number - Sales generated',
      createdAt: 'timestamp - When link was created',
      lastUsed: 'timestamp - Last time link was used'
    }
  }
};

export default {
  AFFILIATE_TIERS,
  AFFILIATE_STATUS,
  COMMISSION_STATUS,
  PAYOUT_METHODS,
  generateAffiliateId,
  generateReferralCode,
  createAffiliateProfile,
  getAffiliateProfile,
  updateAffiliateStats,
  updateAffiliateProfile,
  createCommissionRecord,
  trackReferral,
  getAffiliateCommissions,
  getAffiliateReferrals,
  calculateCommission,
  createAffiliateLink,
  validateAffiliateCode,
  AFFILIATE_SCHEMA
};
